import React, { useState } from 'react';
import { StarIcon } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { Card, EmptyState, Metric } from '../../components/ui/Panels';
import { Button } from '../../components/ui/Button';
import { Avatar } from '../../components/ui/Avatar';
import { Rating } from '../../components/ui/Rating';
import { Textarea } from '../../components/ui/Field';
import { useSession } from '../../contexts/SessionContext';
import { useLocale } from '../../contexts/LocaleContext';
import { averageScores, reviewsForChef } from '../../data/reviews';
import { dateWithYear } from '../../utils/format';
const scoreLabels: [keyof ReturnType<typeof averageScores>, string][] = [['food', 'Food'], ['punctuality', 'Punctuality'], ['cleanliness', 'Cleanliness'], ['communication', 'Communication']];
export function Reviews() {
  const {
    chef
  } = useSession();
  const {
    locale
  } = useLocale();
  const list = reviewsForChef(chef.id);
  const scores = averageScores(chef.id);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replies, setReplies] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState('');
  const distribution = [5, 4, 3, 2, 1].map(stars => ({
    stars,
    count: list.filter(r => Math.round(r.rating) === stars).length
  }));
  return <AppShell title="Reviews" subtitle={`${chef.rating.toFixed(1)} across ${chef.reviewCount} reviews`} data-id="element-1142" data-mp="1czogfowg0hhp">
      <Card className="grid gap-6 p-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1143" data-mp="y6kznjqoe7s1">
        {scoreLabels.map(([key, label]) => <Metric key={key} label={label} value={scores[key].toFixed(1)} detail="out of 5" tone={key === 'food' ? 'primary' : 'default'} data-id="element-1144" data-mp="p7zedgttkenk" />)}
      </Card>

      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]" data-id="element-1145" data-mp="t9uhmqvb923z">
        <div data-id="element-1146" data-mp="fauvpqz6n1bi">
          {list.length ? <ul className="space-y-4" data-id="element-1147" data-mp="fc5wdhxbzsye">
              {list.map(review => {
            const reply = replies[review.id] ?? review.reply;
            return <Card as="li" key={review.id} className="p-5" data-id="element-1148" data-mp="2lzn08fubgzfa">
                    <div className="flex flex-wrap items-start gap-3" data-id="element-1149" data-mp="2h73mnivmq4ah">
                      <Avatar name={review.author} src={review.authorPhoto} size="sm" data-id="element-1150" data-mp="2d5rx1dcr9j2a" />
                      <div className="min-w-0 flex-1" data-id="element-1151" data-mp="2d3dzmxulrurk">
                        <p className="text-sm font-semibold text-ink" data-id="element-1152" data-mp="287ypafvt0gzw">{review.author}</p>
                        <p className="text-[12px] text-ink-3" data-id="element-1153" data-mp="34vwmvrjaayaw">
                          {review.occasion} · {dateWithYear(review.date, locale)}
                        </p>
                      </div>
                      <Rating value={review.rating} data-id="element-1154" data-mp="2vv6wx4gpg253" />
                    </div>

                    <p className="mt-3 text-[15px] leading-relaxed text-ink-2" data-id="element-1155" data-mp="2qxoln7f7tatx">{review.body}</p>

                    <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-1" data-id="element-1156" data-mp="1khm9yjy8bhza">
                      {scoreLabels.map(([key, label]) => <div key={key} className="flex items-baseline gap-1.5" data-id="element-1157" data-mp="1fh88jkr4v6a8">
                          <dt className="text-[12px] text-ink-3" data-id="element-1158" data-mp="15f4czb6vwbmn">{label}</dt>
                          <dd className="tnum text-[12px] font-semibold text-ink" data-id="element-1159" data-mp="1acwd2shon41x">{review.scores[key]}</dd>
                        </div>)}
                    </dl>

                    {reply ? <div className="mt-4 rounded-lg bg-oat p-3.5" data-id="element-1160" data-mp="1k37muvo1s7gq">
                        <p className="text-[12px] font-semibold text-ink" data-id="element-1161" data-mp="1o4ko0sg73mhb">Your reply</p>
                        <p className="mt-1 text-[14px] leading-relaxed text-ink-2" data-id="element-1162" data-mp="1o6x93eh92cyl">{reply}</p>
                      </div> : replyTo === review.id ? <div className="mt-4" data-id="element-1163" data-mp="1y3ibrt5srbz9">
                        <Textarea rows={3} value={draft} onChange={e => setDraft(e.target.value)} aria-label={`Reply to ${review.author}`} placeholder="Guests read replies more closely than the review itself. Keep it short and warm." data-id="element-1164" data-mp="231jtoarhl595" />
                        <div className="mt-2 flex gap-2" data-id="element-1165" data-mp="1u636alyxufdp">
                          <Button type="button" variant="primary" size="sm" disabled={draft.trim().length < 4} onClick={() => {
                    setReplies({
                      ...replies,
                      [review.id]: draft.trim()
                    });
                    setDraft('');
                    setReplyTo(null);
                  }} data-id="element-1166" data-mp="1u5jn71xjxky3">
                            Post reply
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => setReplyTo(null)} data-id="element-1167" data-mp="1keyozflsiifp">
                            Cancel
                          </Button>
                        </div>
                      </div> : <Button type="button" variant="outline" size="sm" className="mt-4" onClick={() => {
                setReplyTo(review.id);
                setDraft('');
              }} data-id="element-1168" data-mp="33ey0ogvlmot3">
                        Reply publicly
                      </Button>}
                  </Card>;
          })}
            </ul> : <EmptyState icon={<StarIcon className="h-5 w-5" aria-hidden="true" data-id="element-1170" data-mp="3r2622npwpdpu" />} title="No reviews yet" body="Guests can only review a booking they completed, so the first one arrives after your first dinner." data-id="element-1169" data-mp="3r2z6hi1e7r4t" />}
        </div>

        <aside className="space-y-4" data-id="element-1171" data-mp="21okix1242aaw">
          <Card className="p-5" data-id="element-1172" data-mp="21rouwkltiac4">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1173" data-mp="26oyj5wxh0fyd">Spread</h2>
            <ul className="mt-3 space-y-2" data-id="element-1174" data-mp="1mmkfgh61n42h">
              {distribution.map(row => <li key={row.stars} className="flex items-center gap-2.5" data-id="element-1175" data-mp="1ik7avw319c7a">
                  <span className="tnum w-3 text-[12px] text-ink-2" data-id="element-1176" data-mp="1in3b5r2cscyw">{row.stars}</span>
                  <StarIcon className="h-3 w-3 fill-accent text-accent" aria-hidden="true" data-id="element-1177" data-mp="1drw9spwk3kd5" />
                  <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-oat" data-id="element-1178" data-mp="18stgg2pxv2l0">
                    <span className="block h-full rounded-full bg-primary" style={{
                  width: list.length ? `${row.count / list.length * 100}%` : '0%'
                }} data-id="element-1179" data-mp="13xe1t8k3x2nj" />
                  </span>
                  <span className="tnum w-4 text-right text-[12px] text-ink-3" data-id="element-1180" data-mp="pwbfcup79qc3">{row.count}</span>
                </li>)}
            </ul>
          </Card>

          <Card className="p-5" data-id="element-1181" data-mp="2p8e5uow8rkz9">
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-1182" data-mp="2kacfdiwhkjdr">Weakest subscore</h2>
            <p className="mt-2 font-display text-lg font-semibold text-ink" data-id="element-1183" data-mp="2ru3q2n6ihmhl">Punctuality · {scores.punctuality}</p>
            <p className="mt-1.5 text-[13px] leading-relaxed text-ink-2" data-id="element-1184" data-mp="2wrn7fvyiafdr">
              Two guests mentioned arrival time. Padding your quoted setup window by 30 minutes is usually enough to fix
              this permanently.
            </p>
          </Card>
        </aside>
      </div>
    </AppShell>;
}